import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const STATUS_OPTIONS = ['pending', 'completed', 'disputed', 'missed', 'skipped', 'cancelled'];

const STATUS_INFO = {
  pending:   { ar: 'قيد الانتظار', en: 'Pending',   bg: 'rgba(245,158,11,0.15)', color: '#fcd34d', icon: '⏳' },
  completed: { ar: 'تمت',          en: 'Completed', bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7', icon: '✅' },
  disputed:  { ar: 'معترض عليها',  en: 'Disputed',  bg: 'rgba(239,68,68,0.15)',  color: '#fca5a5', icon: '⚠️' },
  missed:    { ar: 'فائتة',         en: 'Missed',    bg: 'rgba(100,116,139,0.2)', color: '#94a3b8', icon: '⌛' },
  skipped:   { ar: 'متخطاة',       en: 'Skipped',   bg: 'rgba(100,116,139,0.2)', color: '#cbd5e1', icon: '↩️' },
  cancelled: { ar: 'ملغاة',         en: 'Cancelled', bg: 'rgba(239,68,68,0.1)',  color: '#fca5a5', icon: '🚫' },
};

const VSMSchedule = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const T = {
    title:           { ar: '📅 جدول اليوم', en: '📅 Daily Schedule' },
    today:           { ar: 'اليوم', en: 'Today' },
    tomorrow:        { ar: 'غداً', en: 'Tomorrow' },
    pickDate:        { ar: 'اختر تاريخ', en: 'Pick date' },
    weekView:        { ar: 'نظرة الأسبوع', en: 'Week Overview' },
    refresh:         { ar: '🔄 تحديث', en: '🔄 Refresh' },
    generate:        { ar: '⚡ توليد جدول', en: '⚡ Generate' },
    generating:      { ar: 'جاري التوليد...', en: 'Generating...' },

    statTotal:       { ar: 'الإجمالي', en: 'Total' },
    statPending:     { ar: 'قيد الانتظار', en: 'Pending' },
    statCompleted:   { ar: 'تمت', en: 'Completed' },
    statDisputed:    { ar: 'اعتراضات', en: 'Disputed' },

    filterStatus:    { ar: 'كل الحالات', en: 'All statuses' },
    filterAgent:     { ar: 'كل الموظفين', en: 'All agents' },
    unassignedFilter:{ ar: 'غير معيّنة', en: 'Unassigned' },

    time:            { ar: 'الوقت', en: 'Time' },
    plate:           { ar: 'اللوحة', en: 'Plate' },
    client:          { ar: 'العميل', en: 'Client' },
    service:         { ar: 'الخدمة', en: 'Service' },
    agent:           { ar: 'الموظف', en: 'Agent' },
    status:          { ar: 'الحالة', en: 'Status' },
    actions:         { ar: 'الإجراءات', en: 'Actions' },

    unassigned:      { ar: '— غير معيّن —', en: '— Unassigned —' },
    noTasks:         { ar: 'لا توجد مهام في هذا اليوم', en: 'No tasks for this day' },
    loading:         { ar: 'جاري التحميل...', en: 'Loading...' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    saveError:       { ar: 'فشل الحفظ', en: 'Failed to save' },
    generated:       { ar: 'تم التوليد', en: 'Generated' },

    weekHint:        { ar: 'الأرقام: تمت / الإجمالي', en: 'Numbers: completed / total' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  // ===== State =====
  const todayStr = new Date().toISOString().slice(0, 10);
  const tomorrowStr = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();

  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [statusFilter, setStatusFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ today: {}, next_7_days: [] });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [updating, setUpdating] = useState({});

  // ===== Fetch schedule =====
  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ date: selectedDate, limit: 200 });
    if (statusFilter) params.append('status', statusFilter);
    if (agentFilter) params.append('assigned_to', agentFilter);

    try {
      const res = await fetch(`${API}/schedule?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setSchedule(data.data);
    } catch (err) {
      console.error('[VSM] Fetch schedule error:', err);
    }
    setLoading(false);
  }, [selectedDate, statusFilter, agentFilter]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // ===== Fetch agents =====
  const fetchAgents = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/schedule/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAgents(data.data);
    } catch (err) {
      console.error('[VSM] Fetch agents error:', err);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // ===== Fetch stats =====
  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/schedule/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('[VSM] Fetch stats error:', err);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ===== Update entry =====
  const updateEntry = async (id, payload) => {
    setUpdating(prev => ({ ...prev, [id]: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/schedule/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        // Update locally for instant feedback
        setSchedule(prev => prev.map(r => r.id === id ? { ...r, ...payload } : r));
        fetchStats();
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      alert(tr('saveError'));
    }
    setUpdating(prev => ({ ...prev, [id]: false }));
  };

  const handleAssignAgent = (id, agentId) => {
    const value = agentId === '' ? null : parseInt(agentId, 10);
    const agentName = value ? (agents.find(a => a.id === value)?.full_name || '') : null;
    updateEntry(id, { assigned_to: value, assigned_agent_name: agentName });
  };

  const handleStatusChange = (id, status) => {
    updateEntry(id, { status });
  };

  // ===== Generate =====
  const handleGenerate = async () => {
    setGenerating(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/schedule/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ days_ahead: 7 })
      });
      const data = await res.json();
      if (data.success) {
        alert(`${tr('generated')}\n\n${data.message}`);
        fetchSchedule();
        fetchStats();
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      alert(tr('saveError'));
    }
    setGenerating(false);
  };

  // ===== Quick date buttons =====
  const isToday = selectedDate === todayStr;
  const isTomorrow = selectedDate === tomorrowStr;

  // ===== Group schedule by time =====
  const sortedSchedule = useMemo(() => {
    return [...schedule].sort((a, b) => {
      const at = a.scheduled_time || '99:99';
      const bt = b.scheduled_time || '99:99';
      return at.localeCompare(bt);
    });
  }, [schedule]);

  const todayStats = stats.today || {};

  // ===== Styles =====
  const inputStyle = {
    padding: '10px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const tableHeaderStyle = {
    padding: '10px 12px', fontSize: '12px', color: '#94a3b8',
    fontWeight: '600', textAlign: isRTL ? 'right' : 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  };
  const tableCellStyle = {
    padding: '10px 12px', fontSize: '13px', color: '#e2e8f0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    verticalAlign: 'middle'
  };

  const formatDateLabel = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB', {
      weekday: 'short', day: '2-digit', month: 'short'
    });
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1400px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
            {tr('title')}
          </h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => { fetchSchedule(); fetchStats(); }}
              style={{
                background: 'rgba(99,102,241,0.15)', color: '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '9px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: 'pointer', fontWeight: '600'
              }}
            >{tr('refresh')}</button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                background: generating ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                color: '#fff', border: 'none',
                padding: '9px 18px', borderRadius: '8px',
                fontWeight: '600', fontSize: '13px',
                cursor: generating ? 'not-allowed' : 'pointer'
              }}
            >{generating ? tr('generating') : tr('generate')}</button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px', marginBottom: '20px'
        }}>
          {[
            { label: tr('statTotal'),     value: todayStats.total || 0,     color: '#a5b4fc', bg: 'rgba(99,102,241,0.12)' },
            { label: tr('statPending'),   value: todayStats.pending || 0,   color: '#fcd34d', bg: 'rgba(245,158,11,0.12)' },
            { label: tr('statCompleted'), value: todayStats.completed || 0, color: '#6ee7b7', bg: 'rgba(16,185,129,0.12)' },
            { label: tr('statDisputed'),  value: todayStats.disputed || 0,  color: '#fca5a5', bg: 'rgba(239,68,68,0.12)' },
          ].map((card, idx) => (
            <div key={idx} style={{
              background: card.bg, borderRadius: '10px',
              padding: '16px 18px',
              border: `1px solid ${card.color}33`
            }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '6px' }}>
                {card.label}
              </div>
              <div style={{ fontSize: '28px', fontWeight: '800', color: card.color }}>
                {Number(card.value)}
              </div>
            </div>
          ))}
        </div>

        {/* Week Overview */}
        {stats.next_7_days && stats.next_7_days.length > 0 && (
          <div style={{
            background: 'rgba(15,23,42,0.5)', borderRadius: '12px',
            padding: '14px 16px', marginBottom: '16px',
            border: '1px solid rgba(255,255,255,0.06)'
          }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', marginBottom: '10px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📊 {tr('weekView')}</span>
              <span style={{ fontSize: '10px', color: '#64748b' }}>{tr('weekHint')}</span>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {stats.next_7_days.map(d => {
                const isSelected = d.scheduled_date === selectedDate;
                const completionRate = d.total > 0 ? d.completed / d.total : 0;
                return (
                  <button
                    key={d.scheduled_date}
                    onClick={() => setSelectedDate(d.scheduled_date)}
                    style={{
                      flex: '1 1 110px',
                      minWidth: '100px',
                      padding: '10px 8px',
                      borderRadius: '8px',
                      border: isSelected ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.08)',
                      background: isSelected ? 'rgba(99,102,241,0.15)' : 'rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>
                      {formatDateLabel(d.scheduled_date)}
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#f1f5f9', marginTop: '4px' }}>
                      {d.completed}/{d.total}
                    </div>
                    {/* Mini progress bar */}
                    <div style={{
                      marginTop: '6px',
                      height: '3px',
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${completionRate * 100}%`,
                        height: '100%',
                        background: completionRate === 1 ? '#10b981' : '#6366f1',
                        transition: 'width 0.3s'
                      }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters Bar */}
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Quick date buttons */}
          <button
            onClick={() => setSelectedDate(todayStr)}
            style={{
              ...inputStyle,
              background: isToday ? 'rgba(99,102,241,0.25)' : 'transparent',
              border: isToday ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.15)',
              color: isToday ? '#a5b4fc' : '#94a3b8',
              cursor: 'pointer', fontWeight: '600',
              padding: '8px 14px'
            }}
          >📅 {tr('today')}</button>
          <button
            onClick={() => setSelectedDate(tomorrowStr)}
            style={{
              ...inputStyle,
              background: isTomorrow ? 'rgba(99,102,241,0.25)' : 'transparent',
              border: isTomorrow ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.15)',
              color: isTomorrow ? '#a5b4fc' : '#94a3b8',
              cursor: 'pointer', fontWeight: '600',
              padding: '8px 14px'
            }}
          >➡️ {tr('tomorrow')}</button>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{ ...inputStyle, maxWidth: '180px' }}
          />

          <div style={{ flex: 1 }} />

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer', minWidth: '160px' }}
          >
            <option value="">{tr('filterStatus')}</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>
                {STATUS_INFO[s].icon} {STATUS_INFO[s][lang]}
              </option>
            ))}
          </select>

          <select
            value={agentFilter}
            onChange={e => setAgentFilter(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer', minWidth: '180px' }}
          >
            <option value="">{tr('filterAgent')}</option>
            <option value="unassigned">{tr('unassignedFilter')}</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.full_name}</option>
            ))}
          </select>
        </div>

        {/* Schedule Table */}
        <div style={{
          background: 'rgba(30,41,59,0.5)', borderRadius: '12px',
          overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              {tr('loading')}
            </div>
          ) : sortedSchedule.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              {tr('noTasks')}
            </div>
          ) : (
            <div style={{ overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'rgba(15,23,42,0.5)' }}>
                  <tr>
                    <th style={{ ...tableHeaderStyle, width: '70px' }}>{tr('time')}</th>
                    <th style={tableHeaderStyle}>{tr('plate')}</th>
                    <th style={tableHeaderStyle}>{tr('client')}</th>
                    <th style={tableHeaderStyle}>{tr('service')}</th>
                    <th style={tableHeaderStyle}>{tr('agent')}</th>
                    <th style={tableHeaderStyle}>{tr('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSchedule.map(row => {
                    const statusInfo = STATUS_INFO[row.status] || STATUS_INFO.pending;
                    const isUpdating = !!updating[row.id];

                    return (
                      <tr key={row.id}
                          style={{
                            transition: 'background 0.15s',
                            opacity: isUpdating ? 0.5 : 1
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.04)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                        <td style={tableCellStyle}>
                          <span style={{
                            fontFamily: 'monospace', fontWeight: '700',
                            color: '#a5b4fc', fontSize: '13px'
                          }} dir="ltr">
                            {row.scheduled_time ? row.scheduled_time.slice(0, 5) : '—'}
                          </span>
                        </td>

                        <td style={tableCellStyle}>
                          {row.plate_letters_en ? (
                            <div style={{
                              display: 'inline-block',
                              background: 'linear-gradient(180deg, #fff, #e5e5e5)',
                              border: '2px solid #1a1a2e',
                              borderRadius: '4px',
                              padding: '3px 8px',
                              fontFamily: 'monospace',
                              fontWeight: '900',
                              fontSize: '12px',
                              color: '#1a1a2e',
                              minWidth: '90px',
                              textAlign: 'center'
                            }} dir="ltr">
                              <div style={{ fontSize: '10px' }}>
                                {row.plate_letters_en} / {row.plate_letters_ar}
                              </div>
                              <div style={{ fontSize: '13px', marginTop: '1px' }}>
                                {row.plate_numbers}
                              </div>
                            </div>
                          ) : '—'}
                          {row.make && (
                            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                              {row.make} {row.model}
                            </div>
                          )}
                        </td>

                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: '600' }}>
                            {isRTL && row.client_name_ar ? row.client_name_ar : row.client_name}
                          </div>
                          {row.client_phone && (
                            <div style={{ fontSize: '10px', color: '#64748b' }} dir="ltr">
                              {row.client_phone}
                            </div>
                          )}
                        </td>

                        <td style={tableCellStyle}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                          }}>
                            <span style={{
                              width: '8px', height: '8px', borderRadius: '50%',
                              background: row.service_color || '#6366f1', display: 'inline-block'
                            }} />
                            {isRTL ? row.service_name_ar_snapshot : row.service_name_snapshot}
                          </span>
                          {row.price_snapshot != null && (
                            <div style={{ fontSize: '10px', color: '#10b981', marginTop: '2px', fontWeight: '600' }}>
                              {parseFloat(row.price_snapshot).toFixed(2)} SAR
                            </div>
                          )}
                        </td>

                        <td style={tableCellStyle}>
                          <select
                            value={row.assigned_to || ''}
                            onChange={e => handleAssignAgent(row.id, e.target.value)}
                            disabled={isUpdating}
                            style={{
                              ...inputStyle,
                              padding: '6px 8px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              minWidth: '140px',
                              background: row.assigned_to ? 'rgba(99,102,241,0.1)' : 'rgba(245,158,11,0.08)',
                              border: row.assigned_to
                                ? '1px solid rgba(99,102,241,0.3)'
                                : '1px solid rgba(245,158,11,0.3)'
                            }}
                          >
                            <option value="">{tr('unassigned')}</option>
                            {agents.map(a => (
                              <option key={a.id} value={a.id}>{a.full_name}</option>
                            ))}
                          </select>
                        </td>

                        <td style={tableCellStyle}>
                          <select
                            value={row.status}
                            onChange={e => handleStatusChange(row.id, e.target.value)}
                            disabled={isUpdating}
                            style={{
                              padding: '5px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              background: statusInfo.bg,
                              color: statusInfo.color,
                              border: `1px solid ${statusInfo.color}55`,
                              outline: 'none',
                              minWidth: '120px'
                            }}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s} style={{ background: '#1e293b', color: '#f1f5f9' }}>
                                {STATUS_INFO[s].icon} {STATUS_INFO[s][lang]}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default VSMSchedule;
